import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { humanize, titleize, unescapeHTML } from 'underscore.string';

import LocationAutosuggest from 'components/site/location-autosuggest';
import ErrorAlert from 'components/ui/ErrorAlert';
import CustomerSearch from 'components/site/customer-search';
import { withTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';

class LocationPairForm extends Component {
  static propTypes = {
    onCancel: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    locationPair: PropTypes.shape({
      origin_name: PropTypes.string,
      origin_location: PropTypes.object,
      destination_name: PropTypes.string,
      destination_location: PropTypes.object,
      shipper: PropTypes.object,
    }),
  };

  constructor(props) {
    super(props);
    this.state = {
      ...getStateFromProps(props),
      error: {},
    };
  }

  fieldHasError(field) {
    return this.state.error.hasOwnProperty(field);
  }

  handleShipperFieldChange = () => {
    this.setState({
      shipper: {
        company_name: '',
        _id: null,
      },
    });
  };

  handleShipperSelect = (item, value) => {
    this.setState({
      shipper: {
        company_name: value.name,
        _id: value._id,
      },
    });
  };

  handleCancel = () => this.props.onCancel();

  handleSave = (e) => {
    e.preventDefault();
    const error = this.validate();
    if (Object.keys(error).length === 0) {
      const payload = transformPayload(this.state);
      this.props.onSave(payload);
    }
  };

  handleAddressSelect = (field) => (item) =>
    this.setState({
      [field]: item,
    });

  handleFieldChange = (field) => (e) =>
    this.setState({ [field]: e.target.value });

  validate = () => {
    const error = {};
    const { t } = this.props;

    const {
      origin_name,
      origin_location,
      destination_name,
      destination_location,
      shipper,
    } = this.state;

    if (!origin_name || origin_name.trim().length === 0) {
      error.origin_name = t('ORIGIN_NAME_REQUIRED');
    }

    if (!origin_location?.address || origin_location.address.trim() === 0) {
      error.origin_location = t('ORIGIN_ADDRESS_REQUIRED');
    }

    if (!destination_name || destination_name.trim().length === 0) {
      error.destination_name = t('DESTINATION_NAME_REQUIRED');
    }

    if (
      !destination_location?.address ||
      destination_location.address.trim() === 0
    ) {
      error.destination_location = t('DESTINATION_ADDRESS_REQUIRED');
    }

    if (!shipper._id) {
      error.shipper = t('SHIPPER_IS_REQUIRED');
    }

    this.setState({
      error,
    });
    return error;
  };

  renderValidationErrors() {
    const { t } = this.props;
    const errorKeys = Object.keys(this.state.error);
    if (errorKeys.length === 0) {
      return null;
    }
    return (
      <div className="w-1/2">
        <ErrorAlert title={t('MISSING_FIELDS')}>
          <ul>
            {errorKeys.map((key) => (
              <li key={key}>{this.state.error[key]}</li>
            ))}
          </ul>
        </ErrorAlert>
      </div>
    );
  }

  renderShipperField() {
    const { t } = this.props;
    const shipperClassName = classnames({
      'md:mr-2': true,
      error: this.fieldHasError('shipper'),
    });
    return (
      <fieldset className={shipperClassName}>
        <label className="mb-2 form-label">
          {titleize(t('SHIPPER', { count: 1 }))}
        </label>
        <div className="customer-search mb-4">
          <CustomerSearch
            clearOnSelect
            onChange={this.handleShipperFieldChange}
            onSelect={this.handleShipperSelect}
            placeholder={t('SEARCH_FOR_SHIPPER')}
            defaultValue={unescapeHTML(this.state.shipper.company_name)}
            type="shippers"
          />
        </div>
      </fieldset>
    );
  }

  renderNameField(field) {
    const { t } = this.props;
    const fieldClassName = classnames({
      'md:mr-2': true,
      error: this.fieldHasError(field),
    });
    const label = titleize(humanize(field));
    return (
      <fieldset className={fieldClassName}>
        <label className="form-label mb-2 block">{t(sluggify(label))}</label>
        <input
          type="text"
          className="form-field mb-4 w-full"
          value={this.state[field]}
          placeholder={t(sluggify(label))}
          onChange={this.handleFieldChange(field)}
        />
      </fieldset>
    );
  }

  renderAddressField(field) {
    const { t } = this.props;
    const fieldClassName = classnames({
      error: this.fieldHasError(field),
    });

    const label = titleize(humanize(field));
    return (
      <fieldset className={fieldClassName}>
        <label className="form-label mb-2 block">{t(sluggify(label))}</label>
        <div className="mb-4">
          <LocationAutosuggest
            value={this.state[field]?.address || null}
            searchField="address"
            callbackSelectedItem={this.handleAddressSelect(field)}
            placeholderText={t('SEARCH_FOR_LOCATION')}
          />
        </div>
      </fieldset>
    );
  }

  render() {
    const { t } = this.props;
    return (
      <div className="">
        <div className="flex flex-wrap">
          <div className="md:w-1/2">{this.renderNameField('origin_name')}</div>
          <div className="md:w-1/2">
            {this.renderAddressField('origin_location')}
          </div>
        </div>
        <hr />
        <div className="flex flex-wrap">
          <div className="md:w-1/2">
            {this.renderNameField('destination_name')}
          </div>
          <div className="md:w-1/2">
            {this.renderAddressField('destination_location')}
          </div>
        </div>
        <div className="flex flex-wrap">
          <div className="md:w-1/2">{this.renderShipperField()}</div>
        </div>
        {this.renderValidationErrors()}
        <div className="form-actions flex justify-end items-center">
          <button className="button button-green" onClick={this.handleSave}>
            {titleize(t('SAVE'))}
          </button>
          <button
            className="button button-secondary"
            onClick={this.handleCancel}
          >
            {titleize(t('CANCEL'))}
          </button>
        </div>
      </div>
    );
  }
}

function transformPayload(state) {
  const {
    origin_location,
    origin_name,
    destination_location,
    destination_name,
    _id,
    shipper: { _id: shipperId },
  } = state;

  return {
    origin: {
      name: origin_name.trim(),
      address: origin_location.address.trim(),
      place_id: origin_location.place_id,
    },
    destination: {
      name: destination_name.trim(),
      address: destination_location.address.trim(),
      place_id: destination_location.place_id,
    },
    shipper: shipperId,
    _id,
  };
}

function getInitialState() {
  return {
    origin_name: '',
    origin_location: {
      address: '',
    },
    destination_name: '',
    destination_location: {
      address: '',
    },
    shipper: {
      name: '',
      _id: null,
    },
  };
}

function getStateFromProps(props) {
  return props.locationPair || getInitialState();
}

export default withTranslation()(LocationPairForm);
